import type { FUSportsMatchWithNativeId, FUSportsStandingsEntryWithNativeTeamId } from "@/utilities/data/types";

/**
 * A match annotated with a badge indicating why it's interesting.
 */
export interface HighlightedMatch extends FUSportsMatchWithNativeId {
    badge?: string;
}

interface MatchScore {
    match: FUSportsMatchWithNativeId;
    score: number;
    badge?: string;
    avgRank: number;
}

/**
 * Picks the most interesting upcoming matches based on standings data.
 *
 * Algorithm:
 * 1. Takes the first 10 upcoming matches as candidates
 * 2. Scores each match based on team positions in standings
 * 3. Returns top `limit` matches with badges indicating why they're interesting
 *
 * Scoring rules:
 * - Both teams in top 3 (title clash): +4 points, "Title Race" badge
 * - Both teams in bottom 3 (relegation battle): +3 points, "Relegation Battle" badge
 * - At least one team in top 5: +3 points, "Top of the Table" badge
 * - Rank difference ≤ 2 (direct rivals): +2 points, "Direct Rivals" badge
 * - Average rank ≤ 6: +1 point
 *
 * Teams not found in standings receive rank Infinity (neutral score).
 * Results are sorted by score descending, with ties broken by lower average rank.
 *
 * @param upcomingMatches - All upcoming matches for the competition
 * @param standings - Current standings entries
 * @param limit - Maximum number of matches to return (default: 2)
 * @returns Array of highlighted matches with optional badges
 */
export function pickHighlightedMatches(
    upcomingMatches: FUSportsMatchWithNativeId[],
    standings: FUSportsStandingsEntryWithNativeTeamId[],
    limit: number = 2
): HighlightedMatch[] {
    // Take first 10 upcoming matches as candidate pool
    const candidates = upcomingMatches.slice(0, 10);

    if (candidates.length === 0) {
        return [];
    }

    // Build lookup map: sportal365TeamId → rank
    const rankMap = new Map<string, number>();
    standings.forEach((entry) => {
        rankMap.set(entry.competitor.id, entry.rank);
    });

    // Get total number of teams in standings for bottom 3 calculation
    const totalTeams = standings.length;

    // Score each match
    const scoredMatches: MatchScore[] = candidates.map((match) => {
        const rank1 = rankMap.get(match.competitorOne.id) ?? Infinity;
        const rank2 = rankMap.get(match.competitorTwo.id) ?? Infinity;

        let score = 0;
        let badge: string | undefined;

        // Both teams in top 3 (title clash)
        if (rank1 <= 3 && rank2 <= 3) {
            score += 4;
            badge = "Title Race";
        }
        // Both teams in bottom 3 (relegation battle)
        else if (totalTeams > 0 && rank1 > totalTeams - 3 && rank2 > totalTeams - 3) {
            score += 3;
            badge = "Relegation Battle";
        }
        // At least one team in top 5
        else if (rank1 <= 5 || rank2 <= 5) {
            score += 3;
            badge = "Top of the Table";
        }
        // Rank difference ≤ 2 (direct rivals)
        else if (Math.abs(rank1 - rank2) <= 2 && rank1 !== Infinity && rank2 !== Infinity) {
            score += 2;
            badge = "Direct Rivals";
        }

        // Average rank ≤ 6
        const avgRank = rank1 === Infinity || rank2 === Infinity ? Infinity : (rank1 + rank2) / 2;
        if (avgRank <= 6) {
            score += 1;
        }

        return {
            match,
            score,
            badge,
            avgRank,
        };
    });

    // Sort by score descending, tiebreak on lower average rank (closer to top)
    scoredMatches.sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        return a.avgRank - b.avgRank;
    });

    // Get top `limit` matches
    const topMatches = scoredMatches.slice(0, limit).map((scored) => ({
        ...scored.match,
        badge: scored.badge,
    }));

    // Sort by kickoff date ascending before returning
    topMatches.sort((a, b) => {
        const dateA = new Date(a.timing.kickoffTime).getTime();
        const dateB = new Date(b.timing.kickoffTime).getTime();
        return dateA - dateB;
    });

    return topMatches;
}

